import * as React from "react";
import DateRange, {RangeToTimes} from "../components/DateRange";
import Loading from "../components/loading";
import {gql} from "@apollo/client";
import {query} from "../lib/apollo";
import {
    Alert, FormControl, FormControlLabel, IconButton, Link as MuiLink,
    MenuItem,
    Paper, Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableSortLabel,
    TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import moment from "moment";
import StripedTableRow from "../components/StripedTableRow";
import TableFooterRow from "../components/TableFooterRow";
import TableHeaderRow from "../components/TableHeaderRow";
import MultiSelect from "../components/multiSelect/multiSelect";
import downloadCsv from "../lib/downloadCsv";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {Link} from "react-router-dom";
import {toast} from "react-toastify";
import {useAuth} from "../providers/auth";
import {PermissionType} from "../objects/UserRead";
import {searchPubs} from "./blockedPublishers/blockedPublishersService";

const ALL_DATA_COLUMNS = [
    {id: "publisher", value: "Publisher"},
    {id: "subject", value: "Subject"},
    {id: "program", value: "Program"},
    {id: "edu_level", value: "Edu Level"},
    {id: "degree_level", value: "Degree Looking"},
    {id: "hs_grad", value: "HS Grad Year"},
    {id: "hs_grad2", value: "HS Grad Year - exact"},
    {id: "keyword", value: "Keyword"},
    {id: "when_start", value: "When Start"},
    {id: "age", value: "Age"},
    {id: "age2", value: "Age - Exact"},
    {id: "state", value: "State"},
    {id: "classes", value: "Classes"},
    {id: "credits", value: "Credits"},
    {id: "working", value: "Working"},
];

const DEFAULT_DATA = [
    {id: "subject", value: "Subject"},
    {id: "program", value: "Program"},
    {id: "degree_level", value: "Degree Looking"},
];

function money(n: number) {
    return Number(n || 0).toLocaleString(undefined, {style: "currency", currency: "USD"});
}

function pct(part: number, whole: number) {
    if (!whole) {
        return 0;
    }
    return (part / whole) * 100;
}

function formatPct(n: number) {
    return `${Number(n || 0).toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%`;
}

const CLASSES = [
    {id: 0, value: "In a classroom"},
    {id: 1, value: "Online"},
    {id: 2, value: "Either way"},
];

export default function InventoryStatsPage() {
    const auth = useAuth();
    const isAdmin = !!auth.user?.permissions.includes(PermissionType.Admin);
    const dataColumns = isAdmin ? ALL_DATA_COLUMNS : ALL_DATA_COLUMNS.filter((c) => c.id !== "publisher");

    React.useEffect(() => {
        document.title = "Inventory Stats | DN";
    }, []);

    const [dateRange, setDateRange] = React.useState([] as any);
    const [filtersLoaded, setFiltersLoaded] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [results, setResults] = React.useState([] as any[]);
    const [columns, setColumns] = React.useState([] as {key: string; label: string}[]);
    const [selectedData, setSelectedData] = React.useState(DEFAULT_DATA as any[]);
    const [selectedPublishers, setSelectedPublishers] = React.useState([] as any[]);
    const [selectedSubjects, setSelectedSubjects] = React.useState([] as any[]);
    const [selectedClasses, setSelectedClasses] = React.useState([] as any[]);
    const [subjects, setSubjects] = React.useState([] as any[]);
    const [totalMin, setTotalMin] = React.useState("1");
    const [zips, setZips] = React.useState("");
    const [military, setMilitary] = React.useState(false);
    const [ageMin, setAgeMin] = React.useState("18");
    const [ageMax, setAgeMax] = React.useState("99");
    const [soldFilter, setSoldFilter] = React.useState("both");
    const [sort, setSort] = React.useState({column: "total", direction: "desc"});

    React.useEffect(() => {
        const init = async () => {
            const d = RangeToTimes("Today");
            setDateRange(d);
            const q = gql`
                query q {
                    inventoryStatsOptions {
                        subjects { id name }
                    }
                }
            `;
            const response = await query(q, {});
            if (response?.inventoryStatsOptions) {
                setSubjects(response.inventoryStatsOptions.subjects.map((s: any) => ({id: s.id, value: s.name})));
            }
            setFiltersLoaded(true);
            await reload(d, DEFAULT_DATA, [], [], [], "1", "", false, "18", "99", "both");
        };
        init().then(() => {});
    }, []);

    const columnValue = (row: any, key: string) => {
        const col = row.columns.find((c: any) => c.dataType === key);
        return col ? col.value : "";
    };

    const sortedResults = React.useMemo(() => {
        const rows = [...results];
        rows.sort((a: any, b: any) => {
            let av: any;
            let bv: any;
            if (sort.column === "rpsl") {
                av = a.sold ? a.revenue / a.sold : 0;
                bv = b.sold ? b.revenue / b.sold : 0;
            } else if (sort.column === "pctSold") {
                av = pct(a.sold, a.total);
                bv = pct(b.sold, b.total);
            } else if (sort.column === "pctUnsold") {
                av = pct(a.unsold, a.total);
                bv = pct(b.unsold, b.total);
            } else if (["total", "scrub", "mediums", "sold", "unsold", "revenue"].includes(sort.column)) {
                av = a[sort.column];
                bv = b[sort.column];
            } else {
                av = columnValue(a, sort.column);
                bv = columnValue(b, sort.column);
            }
            if (sort.column === "publisher_id" || sort.column === "age2" || sort.column === "hs_grad2") {
                av = Number(av);
                bv = Number(bv);
            } else if (typeof av === "string") {
                av = av.toLowerCase();
                bv = String(bv).toLowerCase();
            }
            if (sort.direction === "desc") {
                return av < bv ? 1 : av > bv ? -1 : 0;
            }
            return av > bv ? 1 : av < bv ? -1 : 0;
        });
        return rows;
    }, [results, sort]);

    const totals = React.useMemo(() => {
        const t = {total: 0, scrub: 0, mediums: 0, sold: 0, unsold: 0, revenue: 0, rpsl: 0, pctSold: 0, pctUnsold: 0};
        results.forEach((d: any) => {
            t.total += d.total;
            t.scrub += d.scrub;
            t.mediums += d.mediums;
            t.sold += d.sold;
            t.unsold += d.unsold;
            t.revenue += d.revenue;
        });
        if (t.total > 0) {
            t.scrub = t.scrub / t.total;
        }
        t.rpsl = t.sold > 0 ? t.revenue / t.sold : 0;
        t.pctSold = pct(t.sold, t.total);
        t.pctUnsold = pct(t.unsold, t.total);
        return t;
    }, [results]);

    const reload = async (
        date: any,
        dataSel = selectedData,
        publisherSel = selectedPublishers,
        subjectSel = selectedSubjects,
        classSel = selectedClasses,
        totalMinStr = totalMin,
        zipsStr = zips,
        militaryVal = military,
        ageMinStr = ageMin,
        ageMaxStr = ageMax,
        soldFilterVal = soldFilter,
    ) => {
        if (!dataSel || dataSel.length === 0) {
            toast.error("Select at least one data column");
            return;
        }
        setLoading(true);
        let c = dataSel.map((x: any) => ({key: x.id, label: x.value}));
        if (c.find((x: any) => x.key === "publisher") && !c.find((x: any) => x.key === "publisher_id")) {
            c.push({key: "publisher_id", label: "Publisher ID"});
        }
        setColumns(c);

        const parsedTotal = parseInt(totalMinStr, 10);
        const parsedAgeMin = parseInt(ageMinStr, 10);
        const parsedAgeMax = parseInt(ageMaxStr, 10);

        const q = gql`
            query q($input: InventoryStatInput!) {
                inventoryStats(input: $input) {
                    columns {
                        dataType
                        value
                    }
                    total
                    scrub
                    mediums
                    sold
                    unsold
                    revenue
                }
            }
        `;
        const variables = {
            input: {
                startDate: moment(date[0]).format("YYYY-MM-DD HH:mm:ss"),
                endDate: moment(date[1]).format("YYYY-MM-DD HH:mm:ss"),
                data: c.map((x: any) => x.key),
                totalMin: isNaN(parsedTotal) ? undefined : parsedTotal,
                publisherIds: publisherSel.map((x: any) => x.id),
                subjectIds: subjectSel.map((x: any) => x.id),
                zips: zipsStr || undefined,
                military: militaryVal,
                classes: classSel.map((x: any) => x.id),
                ageMin: isNaN(parsedAgeMin) ? undefined : parsedAgeMin,
                ageMax: isNaN(parsedAgeMax) ? undefined : parsedAgeMax,
                soldFilter: soldFilterVal,
            },
        };

        const response = await query(q, variables);
        if (response?.inventoryStats) {
            setResults(response.inventoryStats);
            setSort({column: "total", direction: "desc"});
        } else {
            setResults([]);
        }
        setLoading(false);
    };

    async function getData(search: string, selected: any[]) {
        const s = selected.map((x: any) => x.id);
        return dataColumns.filter((x) => x.value.toLowerCase().includes(search.toLowerCase()) && !s.includes(x.id));
    }

    async function getSubjects(search: string, selected: any[]) {
        const s = selected.map((x: any) => x.id);
        return subjects.filter((x) => x.value.toLowerCase().includes(search.toLowerCase()) && !s.includes(x.id));
    }

    async function getClasses(search: string, selected: any[]) {
        const s = selected.map((x: any) => x.id);
        return CLASSES.filter((x) => x.value.toLowerCase().includes(search.toLowerCase()) && !s.includes(x.id));
    }

    const doSort = (column: string) => {
        if (sort.column === column) {
            setSort({column, direction: sort.direction === "desc" ? "asc" : "desc"});
        } else {
            const ascDefault = !["total", "scrub", "mediums", "sold", "unsold", "pctSold", "pctUnsold", "revenue", "rpsl"].includes(column);
            setSort({column, direction: ascDefault ? "asc" : "desc"});
        }
    };

    const download = async () => {
        const visibleColumns = columns.filter((x) => x.key !== "publisher_id");
        const header = [
            ...visibleColumns.map((c) => c.label),
            "Total",
            "Sold",
            "% Sold",
            "Unsold",
            "% Unsold",
            "Rev",
            "RPSL",
            "Scrub",
            "FL Mediums",
        ];
        const content: any[][] = [header];
        sortedResults.forEach((r: any) => {
            const row: any[] = [];
            visibleColumns.forEach((d) => {
                if (d.key === "publisher") {
                    row.push(`${columnValue(r, "publisher")} (${columnValue(r, "publisher_id")})`);
                } else {
                    row.push(columnValue(r, d.key));
                }
            });
            row.push(r.total);
            row.push(r.sold);
            row.push(pct(r.sold, r.total));
            row.push(r.unsold);
            row.push(pct(r.unsold, r.total));
            row.push(r.revenue);
            row.push(r.sold ? r.revenue / r.sold : 0);
            row.push(r.scrub);
            row.push(r.mediums);
            content.push(row);
        });
        await downloadCsv("inventory.csv", content);
    };

    if (!filtersLoaded) {
        return <Loading/>;
    }

    return (
        <>
            <h2>Inventory Stats</h2>
            <Box sx={{display: "flex", flexDirection: "row", flexWrap: "wrap", alignItems: "flex-end", gap: 1}}>
                <DateRange dateRange={dateRange} setDateRange={(value: any) => setDateRange(value)} style={{marginRight: "10px"}}/>
                <FormControl variant="standard" sx={{width: "140px"}}>
                    <MultiSelect searchFunction={getData} selectedItems={selectedData} setSelectedItems={setSelectedData} placeholder={"Data"}/>
                </FormControl>
                <TextField
                    variant="standard"
                    label="Total >="
                    value={totalMin}
                    onChange={(e) => setTotalMin(e.target.value)}
                    sx={{width: "90px"}}
                />
                {isAdmin && (
                    <FormControl variant="standard" sx={{width: "160px"}}>
                        <MultiSelect searchFunction={searchPubs} selectedItems={selectedPublishers} setSelectedItems={setSelectedPublishers} placeholder={"Publishers"}/>
                    </FormControl>
                )}
                <FormControl variant="standard" sx={{width: "150px"}}>
                    <MultiSelect searchFunction={getSubjects} selectedItems={selectedSubjects} setSelectedItems={setSelectedSubjects} placeholder={"Subjects"}/>
                </FormControl>
                <TextField
                    select
                    variant="standard"
                    label="Sold"
                    value={soldFilter}
                    onChange={(e) => setSoldFilter(e.target.value)}
                    sx={{width: "110px"}}
                >
                    <MenuItem value="both">Both</MenuItem>
                    <MenuItem value="sold">Sold</MenuItem>
                    <MenuItem value="unsold">Unsold</MenuItem>
                </TextField>
                <TextField
                    variant="standard"
                    label="Zips"
                    multiline
                    minRows={2}
                    maxRows={6}
                    value={zips}
                    onChange={(e) => setZips(e.target.value)}
                    sx={{width: "120px"}}
                />
                <FormControlLabel
                    control={<Switch checked={military} onChange={(e) => setMilitary(e.target.checked)}/>}
                    label="Military Only"
                    sx={{ml: 0.5}}
                />
                <FormControl variant="standard" sx={{width: "140px"}}>
                    <MultiSelect searchFunction={getClasses} selectedItems={selectedClasses} setSelectedItems={setSelectedClasses} placeholder={"Classes"}/>
                </FormControl>
                <Box sx={{display: "flex", alignItems: "flex-end", gap: 0.5}}>
                    <TextField
                        variant="standard"
                        label="Age"
                        value={ageMin}
                        onChange={(e) => setAgeMin(e.target.value)}
                        sx={{width: "70px"}}
                    />
                    <Box sx={{pb: 0.5}}>to</Box>
                    <TextField
                        variant="standard"
                        label=" "
                        value={ageMax}
                        onChange={(e) => setAgeMax(e.target.value)}
                        sx={{width: "70px"}}
                    />
                </Box>
                <Button
                    onClick={() => reload(dateRange)}
                    variant={"contained"}
                    color={"success"}
                    disabled={loading}
                >
                    Run Report
                </Button>
                {results.length > 0 && (
                    <IconButton aria-label="download" onClick={download} title="Download CSV">
                        <FileDownloadIcon/>
                    </IconButton>
                )}
            </Box>

            <p></p>
            {loading ? <Loading/> :
                results.length === 0 ? <Alert severity="warning">No results found.</Alert> :
                    <TableContainer component={Paper} sx={{maxWidth: "calc(100vw - 10px)"}}>
                        <Table size="small">
                            <TableHead>
                                <TableHeaderRow>
                                    {columns.filter((x) => x.key !== "publisher_id").map((column) => (
                                        <TableCell key={column.key}>
                                            <TableSortLabel
                                                active={sort.column === column.key}
                                                direction={sort.column === column.key && sort.direction === "desc" ? "desc" : "asc"}
                                                onClick={() => doSort(column.key)}
                                            >
                                                {column.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={sort.column === "total"}
                                            direction={sort.column === "total" && sort.direction === "desc" ? "desc" : "asc"}
                                            onClick={() => doSort("total")}
                                        >
                                            Total
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={sort.column === "sold"}
                                            direction={sort.column === "sold" && sort.direction === "desc" ? "desc" : "asc"}
                                            onClick={() => doSort("sold")}
                                        >
                                            Sold
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={sort.column === "pctSold"}
                                            direction={sort.column === "pctSold" && sort.direction === "desc" ? "desc" : "asc"}
                                            onClick={() => doSort("pctSold")}
                                        >
                                            % Sold
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={sort.column === "unsold"}
                                            direction={sort.column === "unsold" && sort.direction === "desc" ? "desc" : "asc"}
                                            onClick={() => doSort("unsold")}
                                        >
                                            Unsold
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={sort.column === "pctUnsold"}
                                            direction={sort.column === "pctUnsold" && sort.direction === "desc" ? "desc" : "asc"}
                                            onClick={() => doSort("pctUnsold")}
                                        >
                                            % Unsold
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={sort.column === "revenue"}
                                            direction={sort.column === "revenue" && sort.direction === "desc" ? "desc" : "asc"}
                                            onClick={() => doSort("revenue")}
                                        >
                                            Rev
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={sort.column === "rpsl"}
                                            direction={sort.column === "rpsl" && sort.direction === "desc" ? "desc" : "asc"}
                                            onClick={() => doSort("rpsl")}
                                        >
                                            RPSL
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={sort.column === "scrub"}
                                            direction={sort.column === "scrub" && sort.direction === "desc" ? "desc" : "asc"}
                                            onClick={() => doSort("scrub")}
                                        >
                                            Scrub
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableSortLabel
                                            active={sort.column === "mediums"}
                                            direction={sort.column === "mediums" && sort.direction === "desc" ? "desc" : "asc"}
                                            onClick={() => doSort("mediums")}
                                        >
                                            Mediums
                                        </TableSortLabel>
                                    </TableCell>
                                </TableHeaderRow>
                            </TableHead>
                            <TableBody>
                                {sortedResults.map((row: any, i: number) => (
                                    <StripedTableRow key={i}>
                                        {columns.filter((x) => x.key !== "publisher_id").map((column) => (
                                            <TableCell key={column.key}>
                                                {column.key === "publisher" ? (
                                                    <MuiLink component={Link} to={`/publishers/${columnValue(row, "publisher_id")}`}>
                                                        {columnValue(row, "publisher")} ({columnValue(row, "publisher_id")})
                                                    </MuiLink>
                                                ) : columnValue(row, column.key)}
                                            </TableCell>
                                        ))}
                                        <TableCell align="right">
                                            {row.total.toLocaleString(undefined, {maximumFractionDigits: 0})}
                                        </TableCell>
                                        <TableCell align="right">
                                            {Number(row.sold).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatPct(pct(row.sold, row.total))}
                                        </TableCell>
                                        <TableCell align="right">
                                            {Number(row.unsold).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatPct(pct(row.unsold, row.total))}
                                        </TableCell>
                                        <TableCell align="right">
                                            {money(row.revenue)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {money(row.sold ? row.revenue / row.sold : 0)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {Number(row.scrub).toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%
                                        </TableCell>
                                        <TableCell align="right">
                                            {Number(row.mediums).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                        </TableCell>
                                    </StripedTableRow>
                                ))}
                            </TableBody>
                            <TableFooter>
                                <TableFooterRow>
                                    <TableCell colSpan={columns.filter((x) => x.key !== "publisher_id").length}>Totals</TableCell>
                                    <TableCell align="right">{totals.total.toLocaleString(undefined, {maximumFractionDigits: 0})}</TableCell>
                                    <TableCell align="right">{totals.sold.toLocaleString(undefined, {maximumFractionDigits: 0})}</TableCell>
                                    <TableCell align="right">{formatPct(totals.pctSold)}</TableCell>
                                    <TableCell align="right">{totals.unsold.toLocaleString(undefined, {maximumFractionDigits: 0})}</TableCell>
                                    <TableCell align="right">{formatPct(totals.pctUnsold)}</TableCell>
                                    <TableCell align="right">{money(totals.revenue)}</TableCell>
                                    <TableCell align="right">{money(totals.rpsl)}</TableCell>
                                    <TableCell align="right">{totals.scrub.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%</TableCell>
                                    <TableCell align="right">{totals.mediums.toLocaleString(undefined, {maximumFractionDigits: 0})}</TableCell>
                                </TableFooterRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
            }
        </>
    );
}
